import { useCallback, useMemo, useState } from "react";

import { keyBy, sortBy } from "lodash";
import { useToasts } from "react-toast-notifications";

import { GenericTooltip } from "@components/tooltip-container";
import { useNorthbeamMutation } from "@hooks/http";
import {
  FETCH_USER_DETAILS,
  useUserEnrichment,
} from "@hooks/http/use-user-enrichment";
import {
  type UpdateInputAttributionOptions,
  type UpdateInputAttributionOptionsVariables,
} from "@north-beam/nb-client-side";
import {
  type AttributionModelInput,
  type AttributionWindowInput,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";
import { Checkbox } from "@shared/checkbox";
import { Modal } from "@shared/modal";

import { CvePartnersList } from "./cve-partners-list";
import { UPSERT_INPUT_ATTRIBUTION_OPTIONS } from "./queries";
import { useCveConsentForPartners } from "./use-cve-consent-for-partners";

export function ModelSettings() {
  const { addToast } = useToasts();

  const [confirmationModalOpen, setConfirmationModal] = useState(false);

  const [upsertAttributionOptions, { loading: upsertLoading }] =
    useNorthbeamMutation<
      UpdateInputAttributionOptions,
      UpdateInputAttributionOptionsVariables
    >(UPSERT_INPUT_ATTRIBUTION_OPTIONS, {
      onCompleted: () => {
        addToast(
          "Attribution Model and Window changes will be propagated in your next Processing Run",
          { appearance: "success" },
        );
      },
      refetchQueries: [{ query: FETCH_USER_DETAILS }],
    });

  const { userEnrichment } = useUserEnrichment();
  const {
    cveConsentForPartners,
    onChangeConsent,
    getPartnerDisplayName,
    canViewConsent: canViewCveConsent,
    onSubmitUpdatedConsent,
    preferredCvePartner,
    onChangePreferredCvePartner,
    canToggleConsent: canToggleCveConsent,
    canSubmitConsent: canSubmitCdvConsent,
    isLoadingCveConsent,
    isUsingCrossPublisherCDV,
  } = useCveConsentForPartners();

  const attributionModelOptions = useMemo(() => {
    return sortBy(
      userEnrichment?.attribution.attributionModelOptions.map((a) => {
        return {
          label: a.attribution_model_label,
          value: a,
          disabled: a.always_on,
        };
      }) ?? [],
      "label",
    );
  }, [userEnrichment?.attribution.attributionModelOptions]);

  const currentModelsByModel = useMemo(() => {
    return keyBy(
      attributionModelOptions.map((a) => {
        return a.value;
      }),
      "attribution_model_name",
    );
  }, [attributionModelOptions]);

  const [proposedAttributionModelState, setProposedAttributionModelState] =
    useState(currentModelsByModel);

  // Windows
  const attributionWindowOptions = useMemo(() => {
    return (
      userEnrichment?.attribution.attributionWindowDaysOptions.map((a) => {
        return {
          label: `${a.attribution_window_days} day`,
          value: a,
          disabled: a.always_on,
        };
      }) ?? []
    );
  }, [userEnrichment?.attribution.attributionWindowDaysOptions]);

  const currentWindowsByWindow = useMemo(() => {
    return keyBy(
      attributionWindowOptions.map((a) => {
        return a.value;
      }),
      "attribution_window_days",
    );
  }, [attributionWindowOptions]);

  const [proposedAttributionWindowState, setProposedAttributionWindowState] =
    useState(currentWindowsByWindow);

  const isDisplayPartnersForCVEConsent =
    canViewCveConsent &&
    proposedAttributionModelState["northbeam_custom__enh"]?.state;

  const isCveModel = (modelName: string) =>
    modelName === "northbeam_custom__enh";

  const shouldDisplayModel = (modelName: string) =>
    !isCveModel(modelName) || canViewCveConsent;

  const canToggleModel = (modelName: string) =>
    !isCveModel(modelName) || canToggleCveConsent;

  const onSave = useCallback(async () => {
    const windowsPayload: AttributionWindowInput[] = Object.values(
      proposedAttributionWindowState,
    ).map((i) => {
      return {
        attribution_window_days: i.attribution_window_days,
        state: i.state,
      };
    });

    const modelsPayload: AttributionModelInput[] = Object.values(
      proposedAttributionModelState,
    ).map((i) => {
      return {
        attribution_model_name: i.attribution_model_name,
        state: i.state,
      };
    });

    upsertAttributionOptions({
      variables: {
        attributionWindows: windowsPayload,
        attributionModels: modelsPayload,
      },
    }).finally(() => setConfirmationModal(false));

    isDisplayPartnersForCVEConsent &&
      canToggleCveConsent &&
      onSubmitUpdatedConsent();
  }, [
    proposedAttributionModelState,
    proposedAttributionWindowState,
    upsertAttributionOptions,
    setConfirmationModal,
    isDisplayPartnersForCVEConsent,
    canToggleCveConsent,
    onSubmitUpdatedConsent,
  ]);

  return (
    <div className="container pt-4">
      <h1 className="mx-0 mt-0 mb-0 heading">Model Settings</h1>
      <div className="mt-2 italic">
        The most commonly used attribution models and windows have been enabled
        by default.
      </div>
      <div className="mt-2">
        <div className="font-semibold">Tips:</div>
        <ul className="pl-4 mb-0 list-disc">
          <li className="">
            <span className="font-semibold">Simplify analysis:</span> Only
            enable models and windows used regularly to keep the system fast,
            stable, and easy to manage.
          </li>
          <li>
            <span className="font-semibold">Customization is easy:</span>{" "}
            Turning models or windows temporarily on or off is quick and safe.
            Changes will be applied in your next dashboard update and will be
            fully retroactive.
          </li>
        </ul>
      </div>
      <hr />
      <div className="text-xl">Attribution Models Available</div>
      <div className="grid grid-cols-1 gap-1 mt-2">
        {attributionModelOptions.map((option) => {
          if (!shouldDisplayModel(option.value.attribution_model_name)) {
            return null;
          }

          return (
            <GenericTooltip
              key={option.value.attribution_model_name}
              showTooltip={!canToggleModel(option.value.attribution_model_name)}
              content="Only the account admin can manage this model and its partner consent"
              placement="top-start"
              noInfoCircle
              hideArrow
            >
              <div
                className="flex flex-row gap-1 items-center"
                key={option.label}
              >
                <Checkbox
                  isDisabled={
                    option.value.always_on ||
                    !canToggleModel(option.value.attribution_model_name)
                  }
                  onChange={(val) => {
                    setProposedAttributionModelState((state) => {
                      const current =
                        state[option.value.attribution_model_name];
                      return {
                        ...state,
                        [current.attribution_model_name]: {
                          ...current,
                          state: val,
                        },
                      };
                    });
                  }}
                  value={
                    proposedAttributionModelState[
                      option.value.attribution_model_name
                    ].state
                  }
                />
                <span>{option.label}</span>
              </div>
              {isCveModel(option.value.attribution_model_name) &&
                isDisplayPartnersForCVEConsent && (
                  <CvePartnersList
                    cveConsentForPartners={cveConsentForPartners}
                    preferredCvePartner={preferredCvePartner}
                    canToggleCveConsent={canToggleCveConsent}
                    onChangePreferredCvePartner={onChangePreferredCvePartner}
                    onChangeConsent={onChangeConsent}
                    getPartnerDisplayName={getPartnerDisplayName}
                    isLoadingCveConsent={isLoadingCveConsent}
                    isUsingCrossPublisher={isUsingCrossPublisherCDV}
                  />
                )}
            </GenericTooltip>
          );
        })}
      </div>
      <div className="mt-5" />
      <div className="text-xl">Attribution Windows Available</div>
      <div className="grid grid-cols-1 gap-1 mt-2">
        {attributionWindowOptions.map((option) => {
          return (
            <div className="flex flex-row items-center" key={option.label}>
              <Checkbox
                isDisabled={option.value.always_on}
                onChange={(val) => {
                  setProposedAttributionWindowState((state) => {
                    const current = state[option.value.attribution_window_days];
                    return {
                      ...state,
                      [current.attribution_window_days]: {
                        ...current,
                        state: val,
                      },
                    };
                  });
                }}
                value={
                  proposedAttributionWindowState[
                    option.value.attribution_window_days
                  ].state
                }
              />
              <span className="ml-1">{option.label}</span>
            </div>
          );
        })}
      </div>
      <PrimaryButton
        className="mt-5 w-24"
        isDisabled={
          upsertLoading ||
          (isDisplayPartnersForCVEConsent && !canSubmitCdvConsent)
        }
        loading={upsertLoading}
        onClick={() => setConfirmationModal(true)}
      >
        Save
      </PrimaryButton>
      <Modal
        isOpen={confirmationModalOpen}
        onClose={() => setConfirmationModal(false)}
        headerTitleProps={{ className: "text-xl font-semibold" }}
        titleContent={<div>Are you sure you want to update your account?</div>}
      >
        <ul className="pl-4 list-disc">
          <li className="">All views for all users will be updated.</li>
          <li>
            New integrations, model changes, and breakdown changes will be
            applied next time your data is processed.
          </li>
        </ul>
        <div className="flex space-x-3">
          <PrimaryButton loading={upsertLoading} onClick={onSave}>
            YES, SAVE
          </PrimaryButton>
          <SecondaryButton
            disabled={upsertLoading}
            onClick={() => setConfirmationModal(false)}
          >
            NO, CANCEL
          </SecondaryButton>
        </div>
      </Modal>
    </div>
  );
}
